(() => {
  const initHeader = (root) => {
    if (!root || root.dataset.mgReady === "true") return;
    root.dataset.mgReady = "true";

    const header = root.querySelector(".mg-header");
    const toggle = root.querySelector("[data-mg-menu-toggle]");
    const menu = root.querySelector("[data-mg-mobile-menu]");
    if (!header || !toggle || !menu) return;

    menu.querySelectorAll("details[open]").forEach((details) => { details.open = false; });

    const setOpen = (open) => {
      header.dataset.menuOpen = String(open);
      toggle.setAttribute("aria-expanded", String(open));
      toggle.setAttribute("aria-label", open ? "Chiudi menu" : "Apri menu");
      document.body.classList.toggle("mg-menu-is-open", open);
    };

    toggle.addEventListener("click", () => setOpen(header.dataset.menuOpen !== "true"));
    menu.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => setOpen(false)));
    const desktopClusters = [...root.querySelectorAll(".mg-desktop-nav details")];
    desktopClusters.forEach((cluster) => cluster.addEventListener("toggle", () => {
      if (cluster.open) desktopClusters.forEach((other) => { if (other !== cluster) other.open = false; });
    }));
    document.addEventListener("pointerdown", (event) => {
      if (!root.contains(event.target)) desktopClusters.forEach((cluster) => { cluster.open = false; });
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        desktopClusters.forEach((cluster) => { cluster.open = false; });
      }
    });
    window.addEventListener("resize", () => {
      if (window.innerWidth > 1250) setOpen(false);
    });
  };

  const initHero = (root) => {
    if (!root || root.dataset.mgReady === "true") return;
    root.dataset.mgReady = "true";

    const cards = [...root.querySelectorAll("[data-mg-hero-card]")];
    const dots = [...root.querySelectorAll("[data-mg-hero-dot]")];
    if (!cards.length) return;

    const activate = (index) => {
      cards.forEach((card, cardIndex) => card.classList.toggle("is-active", cardIndex === index));
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle("is-active", dotIndex === index);
        dot.setAttribute("aria-pressed", String(dotIndex === index));
      });
    };

    cards.forEach((card, index) => {
      card.addEventListener("mouseenter", () => activate(index));
      card.addEventListener("focus", () => activate(index));
    });
    dots.forEach((dot, index) => dot.addEventListener("click", () => {
      activate(index);
      cards[index]?.focus({ preventScroll: true });
    }));
    activate(0);
  };

  const initCarousel = (root) => {
    if (!root || root.dataset.mgCarouselReady === "true") return;
    root.dataset.mgCarouselReady = "true";
    const track = root.querySelector("[data-mg-track]");
    const items = [...root.querySelectorAll("[data-mg-carousel-item]")];
    const shell = root.closest("section") || root.parentElement;
    const previous = shell?.querySelector("[data-mg-prev]");
    const next = shell?.querySelector("[data-mg-next]");
    const current = shell?.querySelector("[data-mg-current]");
    const total = shell?.querySelector("[data-mg-total]");
    if (!track || !items.length) return;
    if (total) total.textContent = String(items.length).padStart(2, "0");

    const closestIndex = () => items.reduce((best, item, index) => {
      const distance = Math.abs(item.offsetLeft - track.scrollLeft - track.offsetLeft);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    const goTo = (requestedIndex) => {
      const index = (requestedIndex + items.length) % items.length;
      track.scrollTo({ left: items[index].offsetLeft - track.offsetLeft, behavior: "smooth" });
      if (current) current.textContent = String(index + 1).padStart(2, "0");
    };
    previous?.addEventListener("click", () => goTo(closestIndex() - 1));
    next?.addEventListener("click", () => goTo(closestIndex() + 1));
    let scrollTimer;
    track.addEventListener("scroll", () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        if (current) current.textContent = String(closestIndex() + 1).padStart(2, "0");
      }, 80);
    }, { passive: true });
  };

  const initDrop = (root) => {
    if (!root || root.dataset.mgDropReady === "true") return;
    root.dataset.mgDropReady = "true";
    const end = new Date(root.dataset.mgCountdownDate || "").getTime();
    const parts = { days: root.querySelector("[data-days]"), hours: root.querySelector("[data-hours]"), minutes: root.querySelector("[data-minutes]"), seconds: root.querySelector("[data-seconds]") };
    const update = () => {
      const remaining = Number.isFinite(end) ? Math.max(0, end - Date.now()) : 0;
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining / 3600000) % 24);
      const minutes = Math.floor((remaining / 60000) % 60);
      const seconds = Math.floor((remaining / 1000) % 60);
      Object.entries({ days, hours, minutes, seconds }).forEach(([key, value]) => { if (parts[key]) parts[key].textContent = String(value).padStart(2, "0"); });
    };
    update();
    const timer = window.setInterval(update, 1000);
    root.addEventListener("shopify:section:unload", () => window.clearInterval(timer), { once: true });
    root.querySelectorAll("[data-mg-copy-code]").forEach((button) => button.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(button.dataset.mgCopyCode);
        const label = button.querySelector("span");
        if (label) label.textContent = "COPIATO";
        window.setTimeout(() => { if (label) label.textContent = "COPIA"; }, 1500);
      } catch (_) { /* Clipboard can be unavailable in preview sandboxes. */ }
    }));
  };

  const initShowcase = (root) => {
    if (!root || root.dataset.mgShowcaseReady === "true") return;
    root.dataset.mgShowcaseReady = "true";
    const slides = [...root.querySelectorAll("[data-mg-showcase-slide]")];
    const tabs = [...root.querySelectorAll("[data-mg-showcase-tab]")];
    const current = root.querySelector("[data-mg-showcase-current]");
    const stock = root.querySelector("[data-mg-showcase-stock]");
    const stockLabel = root.querySelector("[data-mg-showcase-unit]");
    if (!slides.length) return;
    let active = 0;
    let timer;
    const show = (requested) => {
      active = (requested + slides.length) % slides.length;
      slides.forEach((slide, index) => slide.classList.toggle("is-active", index === active));
      tabs.forEach((tab, index) => tab.classList.toggle("is-active", index === active));
      if (current) current.textContent = String(active + 1).padStart(2, "0");
      if (stock) stock.textContent = slides[active].dataset.stock || "";
      if (stockLabel) stockLabel.textContent = slides[active].dataset.stockLabel || "";
      window.clearTimeout(timer);
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) timer = window.setTimeout(() => show(active + 1), 7600);
    };
    tabs.forEach((tab, index) => tab.addEventListener("click", () => show(index)));
    root.querySelector("[data-mg-showcase-prev]")?.addEventListener("click", () => show(active - 1));
    root.querySelector("[data-mg-showcase-next]")?.addEventListener("click", () => show(active + 1));
    show(0);
  };

  const initBooster = (root) => {
    if (!root || root.dataset.mgBoosterReady === "true") return;
    root.dataset.mgBoosterReady = "true";
    const targets = [...root.querySelectorAll("[data-mg-fps]")];
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const animate = () => targets.forEach((target, index) => {
      const number = target.querySelector("[data-mg-fps-number]");
      const from = Number(target.dataset.from);
      const to = Number(target.dataset.to);
      if (!number) return;
      if (reduced) { number.textContent = String(to); return; }
      window.setTimeout(() => {
        const started = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - started) / 1300, 1);
          number.textContent = String(Math.round(from + ((to - from) * (1 - Math.pow(1 - progress, 3)))));
          if (progress < 1) window.requestAnimationFrame(tick);
        };
        window.requestAnimationFrame(tick);
      }, index * 110);
    });
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { animate(); observer.disconnect(); } }, { threshold: .25 });
    observer.observe(root);
  };

  const initProcess = (root) => {
    if (!root || root.dataset.mgProcessReady === "true") return;
    root.dataset.mgProcessReady = "true";
    const rail = root.querySelector("[data-mg-process-rail]");
    const steps = [...root.querySelectorAll("[data-mg-process-step]")];
    if (!rail || !steps.length) return;
    let active = 0;
    const activate = (index) => {
      active = index;
      rail.style.setProperty("--position", `${(index / (steps.length - 1)) * 100}%`);
      steps.forEach((step, stepIndex) => {
        step.classList.toggle("is-active", stepIndex === index);
        step.classList.toggle("is-complete", stepIndex < index);
      });
    };
    activate(0);
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) window.setInterval(() => activate((active + 1) % steps.length), 2600);
  };

  const pcProductViews = [
    "dominator-v-2", "dominator-v-1", "blacknova", "crimson", "hectore", "hellfire", "hellstorm",
    "infernus", "madame", "mirage", "nemesis", "perfy", "predator", "ragnarok", "rex", "sinner", "sterminator",
    "stream", "strike", "terminator", "titan", "vanguard", "vega", "vortex", "zeus"
  ];

  const pcViewForPath = (pathname) => {
    const value = String(pathname || "").toLowerCase();
    if (!value.includes("/products/") || value.includes("bundle-terminator")) return "";
    if (value.endsWith("/products/custom-pc-gaming-infernus-ryzen-9-9950x3d-5-7ghz-rtx-5080-16gb-gddr7-64gb-ram-ddr5-6000mhz-rgb-m-2-nmve-1tb")) {
      return "mirage";
    }
    return pcProductViews.find((profile) => {
      const compact = profile.replace(/-v-(\d)$/i, "-v$1");
      return value.includes(`-${profile}-`) || value.endsWith(`-${profile}`) || value.includes(`-${compact}-`) || value.endsWith(`-${compact}`);
    }) || "";
  };

  const applyPcProductViews = (scope = document) => {
    scope.querySelectorAll?.('a[href*="/products/"]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        const profile = pcViewForPath(url.pathname);
        if (!profile || url.searchParams.has("view")) return;
        url.searchParams.set("view", profile);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      } catch (_) { /* Ignore malformed third-party links. */ }
    });

    if (window.Shopify?.designMode) return;
    const current = new URL(window.location.href);
    const profile = pcViewForPath(current.pathname);
    if (!profile || current.searchParams.has("view")) return;
    current.searchParams.set("view", profile);
    window.location.replace(current.toString());
  };

  const initJourneyLinks = (scope = document) => {
    const destinations = [
      [".journey-card--ready", "/collections/tutti-i-pc-gaming", "Apri tutti i PC Gaming pronti"],
      [".journey-card--configurator", "/pages/configuratore-pc", "Apri il configuratore PC"],
      [".journey-card--booster", "/pages/booster-fps", "Scopri Minimal Booster FPS"]
    ];
    destinations.forEach(([selector, destination, label]) => {
      scope.querySelectorAll?.(selector).forEach((card) => {
        if (card.dataset.mgDirectJourney === "true") return;
        card.dataset.mgDirectJourney = "true";
        card.setAttribute("aria-label", label);
        card.addEventListener("click", () => window.location.assign(destination));
      });
    });
  };

  const isRefurbishedNotebookPage = () => {
    const context = [
      window.location.pathname,
      document.title,
      document.body?.dataset?.template,
      document.querySelector("[data-mg-standard-product]")?.textContent?.slice(0, 1200),
      document.querySelector(".mg-content-hero")?.textContent?.slice(0, 500)
    ].filter(Boolean).join(" ").toLowerCase();
    const notebook = /notebook|laptop/.test(context);
    const refurbished = /ricondizionat|rigenerat|refurbished/.test(context);
    return notebook && refurbished;
  };

  const applyWarrantyPolicy = (scope = document) => {
    const months = isRefurbishedNotebookPage() ? 12 : 24;
    document.body?.setAttribute("data-mg-warranty-months", String(months));
    const roots = scope.querySelectorAll?.(".mg-rex-pdp, .mg-standard-pdp, [data-mg-standard-product], [data-mg-rex]") || [];
    roots.forEach((root) => {
      const hasWarrantyContext = (node) => {
        let parent = node.parentElement;
        while (parent && parent !== root) {
          const text = String(parent.textContent || "").trim();
          if (text.length <= 240 && /garanzia/i.test(text) && !/scalapay|klarna|rate|rata/i.test(text)) return true;
          parent = parent.parentElement;
        }
        return false;
      };
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          const parent = node.parentElement;
          if (!parent || parent.closest("script, style, template")) return NodeFilter.FILTER_REJECT;
          const value = node.nodeValue || "";
          const legacyWarranty = /garanzia\s*[23]\s*anni/i.test(value) || (/36\s*mesi/i.test(value) && hasWarrantyContext(node));
          const contextualWarranty = /(?:12|24)\s*mesi/i.test(value) && hasWarrantyContext(node);
          return legacyWarranty || contextualWarranty ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        }
      });
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        node.nodeValue = node.nodeValue
          .replace(/(?:12|24|36)\s*mesi/gi, `${months} MESI`)
          .replace(/garanzia\s*[23]\s*anni/gi, `GARANZIA ${months} MESI`);
      });
    });

    scope.querySelectorAll?.(".mg-rex-assurance__items details[open], .mg-standard-assurance details[open]").forEach((details) => {
      details.open = false;
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll("[data-mg-header]").forEach(initHeader);
    scope.querySelectorAll("[data-mg-hero]").forEach(initHero);
    scope.querySelectorAll("[data-mg-carousel]").forEach(initCarousel);
    scope.querySelectorAll("[data-mg-drop]").forEach(initDrop);
    scope.querySelectorAll("[data-mg-showcase]").forEach(initShowcase);
    scope.querySelectorAll("[data-mg-booster]").forEach(initBooster);
    scope.querySelectorAll("[data-mg-process]").forEach(initProcess);
    initJourneyLinks(scope);
    applyPcProductViews(scope);
    applyWarrantyPolicy(scope);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  window.setTimeout(() => {
    initJourneyLinks(document);
    applyPcProductViews(document);
    applyWarrantyPolicy(document);
  }, 700);
})();

